import { useEffect, useRef } from "react";
import { alpha, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ButtonPages, ArrowPages } from "./PaginatorPages";
import "./paginator.scss";

export const Paginator = ({
  count,
  currentPage,
  onReCallReports,
  limit,
  setLimit,
  listReports,
  maxPageNum,
  minPageNum,
  next,
  offset,
  pageNumLimit,
  previous,
  searchPag,
  setCurrentPage,
  setMaxPageNum,
  setMinPageNum,
  setSearchPag,
}) => {
  /** ------ PAGINATION ------
   * In this section determines the navigation between pages according to the distribution of the registered reports.
   * This, in turn, is determined with the Count/Limit calculation and the index is assigned to each button in the renderPagesNumber function.
   *  eg: 100/10 = 10 (reports per page)
   */

  const [t] = useTranslation("report");
  const theme = useTheme();
  // ref to input search page
  const inputRef1 = useRef(null);
  const inputRef2 = useRef(null);

  // Event on pressing 'Enter' key
  useEffect(() => {
    const keyDown = (evt) => {
      if (
        evt.key === "Enter" &&
        (evt.target === inputRef1.current || evt.target === inputRef2.current)
      ) {
        evt.preventDefault(evt);
        handleSearchPage(evt);
      }
    };
    document.addEventListener("keydown", keyDown);
    return () => {
      document.removeEventListener("keydown", keyDown);
    };
  }, []);

  //Navigate through the navigation pages
  const handlePagination = (pageNumber) => {
    const safePageNumber = Number(pageNumber);
    const newOffset = safePageNumber * limit;
    if (Number.isNaN(newOffset)) return; // evita problemas
    setSearchPag("");
    setCurrentPage(safePageNumber);
    onReCallReports(newOffset);
  };

  // Searching page on input
  const handleSearchPage = (evt) => {
    const pageNumber = Number(evt.target.value) - 1;
    setSearchPag("");
    setCurrentPage(pageNumber);
    const newOffset = pageNumber * limit;
    onReCallReports(newOffset);
    setMaxPageNum(pageNumber + 1);
    setMinPageNum(pageNumber + 1 - pageNumLimit);
  };
  // If there are a next page
  const handleNext = () => {
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    const newOffset = newPage * limit;
    onReCallReports(newOffset);
    if (newPage >= maxPageNum) {
      const newMin = minPageNum + pageNumLimit;
      const newMax = maxPageNum + pageNumLimit;
      setMinPageNum(newMin);
      setMaxPageNum(newMax);
    }
  };
  // If there are a previous page
  const handlePrev = () => {
    const newPage = currentPage - 1;
    setCurrentPage(newPage);
    const newOffset = newPage * limit;
    onReCallReports(newOffset);

    if (newPage < minPageNum) {
      const newMin = minPageNum - pageNumLimit;
      const newMax = maxPageNum - pageNumLimit;
      setMinPageNum(newMin);
      setMaxPageNum(newMax);
    }
  };
  // Go to the next section of pages
  const handleNextSection = () => {
    const newMin = minPageNum + pageNumLimit;
    const newMax = maxPageNum + pageNumLimit;
    const newPage = newMin;
    const newOffset = newPage * limit;
    setMinPageNum(newMin);
    setMaxPageNum(newMax);
    setCurrentPage(newPage);
    onReCallReports(newOffset);
  };
  // Go to previous section of pages
  const handlePrevSection = () => {
    const newMin = minPageNum - pageNumLimit;
    const newMax = maxPageNum - pageNumLimit;
    const newPage = newMax - 1;
    const newOffset = newPage * limit;
    setMinPageNum(newMin);
    setMaxPageNum(newMax);
    setCurrentPage(newPage);
    onReCallReports(newOffset);
  };
  // Go to last page
  const handleLastPage = () => {
    const lastPage = pages.length - 1;
    setMaxPageNum(pages.length);
    setMinPageNum(pages.length - pageNumLimit);
    setCurrentPage(lastPage);
    const newOffset = lastPage * limit;
    onReCallReports(newOffset);
  };
  // Go to first page
  const handleFirstPage = () => {
    setMaxPageNum(pageNumLimit);
    setMinPageNum(0);
    setCurrentPage(0);
    onReCallReports(0);
  };
  // Distribution of results on pages according to selected rows
  const pages = [];
  const page = count / limit;
  for (let i = 0; i < Math.ceil(page); i++) {
    pages.push(i);
  }

  const handleChangeRowsPerPage = (evt) => {
    const newLimit = Number(evt.target.value);
    if (Number.isNaN(newLimit) || newLimit <= 0) return;

    const firstResultIndex = currentPage * limit;
    const newCurrentPage = Math.floor(firstResultIndex / newLimit);
    const newOffset = newCurrentPage * newLimit;

    const newMinPageNum =
      Math.floor(newCurrentPage / pageNumLimit) * pageNumLimit;
    const newMaxPageNum = newMinPageNum + pageNumLimit;

    setLimit(newLimit);
    setCurrentPage(newCurrentPage);
    setMinPageNum(newMinPageNum);
    setMaxPageNum(newMaxPageNum);
    setSearchPag("");

    // Execute search with the new limit
    onReCallReports(newOffset, newLimit);
  };

  return (
    <Stack
      component="nav"
      sx={{
        borderTop: `1px solid ${theme.palette.blueGray50.main}`,
        alignItems: "center",
        justifyContent: "space-around",
        flexFlow: "row wrap",
        py: 2.3,
      }}
      spacing={1}
    >
      {/* Select row per page */}
      <Stack sx={{ flexFlow: "row nowrap" }}>
        <Typography p={0.5} fontSize={11}>
          {t("ReportsList.per_page")}:
        </Typography>
        <select
          className="paginator__select"
          name="limit"
          value={limit}
          onChange={handleChangeRowsPerPage}
        >
          <option value="10">10</option>
          <option value="15">15</option>
          <option value="20">20</option>
        </select>
      </Stack>

      <Stack sx={{ alignItems: "center", flexFlow: "row nowrap" }}>
        {minPageNum >= 1 && (
          <PageDecrement
            handleFirstPage={handleFirstPage}
            searchPag={searchPag}
            pages={pages}
            setSearchPag={setSearchPag}
            inputRef1={inputRef1}
            handlePrevSection={handlePrevSection}
            pageNumLimit={pageNumLimit}
            limit={limit}
            maxPageNum={maxPageNum}
            theme={theme}
          />
        )}
        <ArrowPages onClick={handlePrev} disabled={!previous} icon="&#8249;" />
        <Pages
          pages={pages}
          maxPageNum={maxPageNum}
          minPageNum={minPageNum}
          handlePagination={handlePagination}
          limit={limit}
          currentPage={currentPage}
          theme={theme}
        />
        <ArrowPages onClick={handleNext} disabled={!next} icon="&#8250;" />

        {pages.length > maxPageNum && (
          <PageIncrement
            handleLastPage={handleLastPage}
            handleNextSection={handleNextSection}
            inputRef2={inputRef2}
            limit={limit}
            minPageNum={minPageNum}
            pageNumLimit={pageNumLimit}
            pages={pages}
            searchPag={searchPag}
            setSearchPag={setSearchPag}
            theme={theme}
          />
        )}
      </Stack>
      <Typography p={0.5} fontSize={12}>
        {`${t("ReportsList.results")}: ${
          count === 0 ? 0 : Number(offset) + 1
        } ${t("ReportsList.to")} ${listReports.length + Number(offset)} ${t(
          "ReportsList.of"
        )} ${Number(count)}`}
      </Typography>
    </Stack>
  );
};

const Pages = ({
  pages,
  maxPageNum,
  minPageNum,
  handlePagination,
  currentPage,
  theme,
}) => {
  return pages.map((number) => {
    if (number < maxPageNum && number >= minPageNum) {
      return (
        <ButtonPages
          key={number}
          onClick={() => handlePagination(number)}
          sx={{
            backgroundColor: alpha(
              theme.palette.secondary.main,
              currentPage === number ? 1 : 0.09
            ),
            color:
              currentPage === number
                ? theme.palette.common.white
                : theme.palette.secondary.dark,
          }}
          disabled={currentPage === number}
        >
          {number + 1}
        </ButtonPages>
      );
    }
  });
};

// --- Only if the page limit is greater than 10, the tools are enabled to navigate between results. ---//
const PageDecrement = ({
  handleFirstPage,
  searchPag,
  pages,
  setSearchPag,
  inputRef1,
  handlePrevSection,
  pageNumLimit,
  maxPageNum,
  limit,
  theme,
}) => {
  return (
    <>
      <ButtonPages
        onClick={handleFirstPage}
        name="number"
        value={0}
        sx={{
          backgroundColor: alpha(theme.palette.secondary.main, 0.09),
        }}
      >
        1
      </ButtonPages>
      <input
        type="number"
        name="searchingPage"
        placeholder="..."
        value={searchPag}
        max={pages.length}
        min={1}
        onChange={(evt) => setSearchPag(evt.target.value)}
        className="paginator__searchPage"
        ref={inputRef1}
      />
      <ArrowPages
        onClick={handlePrevSection}
        value={Math.abs(maxPageNum - pageNumLimit - 1) * limit}
        name="section"
        icon="&#8249;&#8249;"
      />
    </>
  );
};

const PageIncrement = ({
  handleLastPage,
  handleNextSection,
  inputRef2,
  limit,
  minPageNum,
  pageNumLimit,
  pages,
  searchPag,
  setSearchPag,
  theme,
}) => {
  return (
    <>
      <ArrowPages
        onClick={handleNextSection}
        value={(pageNumLimit + minPageNum) * limit}
        name="section"
        icon="&#8250;&#8250;"
      />
      <input
        type="number"
        name="searchingPage"
        placeholder="..."
        value={searchPag}
        max={pages.length}
        min={1}
        onChange={(evt) => setSearchPag(evt.target.value)}
        className="paginator__searchPage"
        ref={inputRef2}
      />
      <ButtonPages
        onClick={handleLastPage}
        id={Number(pages.length) - 1}
        name="number"
        value={Math.abs(pages.length - 1) * limit}
        sx={{
          backgroundColor: alpha(theme.palette.secondary.main, 0.09),
        }}
      >
        {pages.length}
      </ButtonPages>
    </>
  );
};
